import { useState, useRef, useEffect, useMemo } from 'react';
import { Check, ChevronDown, X, Search } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Chip } from './Chip';

export interface MultiSelectProps<T> {
  options: T[];
  selected: T[];
  onChange: (selected: T[]) => void;
  getLabel: (item: T) => string;
  getKey: (item: T) => string;
  getSecondaryLabel?: (item: T) => string;
  placeholder?: string;
  searchPlaceholder?: string;
  searchable?: boolean;
  selectAllLabel?: string;
  maxDisplayed?: number;
  disabled?: boolean;
  className?: string;
}

export function MultiSelect<T>({
  options,
  selected,
  onChange,
  getLabel,
  getKey,
  getSecondaryLabel,
  placeholder = 'Select items...',
  searchPlaceholder = 'Search...',
  searchable = true,
  selectAllLabel = 'Select all',
  maxDisplayed = 3,
  disabled = false,
  className,
}: MultiSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedKeys = useMemo(
    () => new Set(selected.map(getKey)),
    [selected, getKey]
  );

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const lowerSearch = search.toLowerCase();
    return options.filter((item) =>
      getLabel(item).toLowerCase().includes(lowerSearch)
    );
  }, [options, search, getLabel]);

  const isAllSelected = selected.length === options.length && options.length > 0;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && searchable && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, searchable]);

  const handleToggle = (item: T) => {
    const key = getKey(item);
    if (selectedKeys.has(key)) {
      onChange(selected.filter((s) => getKey(s) !== key));
    } else {
      onChange([...selected, item]);
    }
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      onChange([]);
    } else {
      onChange([...options]);
    }
  };

  const handleRemove = (item: T) => {
    onChange(selected.filter((s) => getKey(s) !== getKey(item)));
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const displayedItems = selected.slice(0, maxDisplayed);
  const remainingCount = selected.length - maxDisplayed;

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          'flex min-h-[42px] w-full items-center justify-between gap-2 rounded-lg border border-border bg-white px-3 py-2 text-left text-sm transition-all',
          'hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
          disabled && 'cursor-not-allowed opacity-50',
          isOpen && 'border-primary ring-2 ring-primary/20'
        )}
      >
        <div className="flex flex-1 flex-wrap items-center gap-1.5">
          {selected.length === 0 ? (
            <span className="text-muted">{placeholder}</span>
          ) : (
            <>
              {displayedItems.map((item) => (
                <Chip
                  key={getKey(item)}
                  size="sm"
                  variant="selected"
                  removable
                  onRemove={() => handleRemove(item)}
                  onClick={(e) => e.stopPropagation()}
                >
                  {getLabel(item)}
                </Chip>
              ))}
              {remainingCount > 0 && (
                <span className="text-sm text-muted">+{remainingCount} more</span>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-1">
          {selected.length > 0 && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => {
                e.stopPropagation();
                handleClearAll();
              }}
              className="rounded p-1 text-muted hover:bg-gray-200 hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </span>
          )}
          <ChevronDown
            className={cn(
              'h-4 w-4 text-muted transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        </div>
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-2 max-h-80 w-full overflow-hidden rounded-xl border border-border bg-white shadow-lg">
          {searchable && (
            <div className="border-b border-border p-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
                <input
                  ref={inputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full rounded-lg border border-border bg-gray-50 py-2 pl-10 pr-3 text-sm placeholder:text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>
          )}

          <div className="overflow-y-auto" style={{ maxHeight: '240px' }}>
            {options.length > 1 && (
              <button
                type="button"
                onClick={handleSelectAll}
                className="flex w-full items-center gap-3 border-b border-border px-3 py-2.5 text-sm hover:bg-gray-50"
              >
                    <div
                      className={cn(
                        'flex h-5 w-5 items-center justify-center rounded border transition-colors',
                        isAllSelected
                          ? 'border-primary bg-white text-primary'
                          : 'border-border bg-white text-transparent'
                      )}
                    >
                      <Check className="h-3.5 w-3.5" />
                    </div>
                <span className="font-medium">{selectAllLabel}</span>
                <span className="ml-auto text-muted">({options.length})</span>
              </button>
            )}

            {filteredOptions.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-muted">
                No results found
              </div>
            ) : (
              filteredOptions.map((item) => {
                const key = getKey(item);
                const isSelected = selectedKeys.has(key);

                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleToggle(item)}
                    className="flex w-full items-center gap-3 px-3 py-2.5 text-sm hover:bg-gray-50"
                  >
                    <div
                      className={cn(
                        'flex h-5 w-5 items-center justify-center rounded border transition-colors',
                        isSelected
                          ? 'border-primary bg-white text-primary'
                          : 'border-border bg-white text-transparent'
                      )}
                    >
                      <Check className="h-3.5 w-3.5" />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="text-foreground">{getLabel(item)}</div>
                      {getSecondaryLabel && (
                        <div className="text-xs text-muted">
                          {getSecondaryLabel(item)}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
